import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AppUser, UserEnrichment } from "@/domain/auth/authenticated-user";
import { initialAuthState, type AuthSessionState } from "@/domain/auth/auth.state";

const authSlice = createSlice({
  name: "auth",
  initialState: initialAuthState,
  reducers: {
    bootstrapStarted(state) {
      state.status = "loadingProfile";
      state.error = null;
    },

    loginStarted(state) {
      state.status = "authenticating";
      state.error = null;
    },

    sessionEstablished(state, action: PayloadAction<AuthSessionState>) {
      state.session = action.payload;
    },

    profileLoadStarted(state) {
      state.status = "loadingProfile";
      state.error = null;
    },

    profileLoaded(state, action: PayloadAction<AppUser>) {
      state.status = "authenticated";
      state.user = action.payload;
      state.error = null;
      state.profileFetchedAt = new Date().toISOString();
    },

    profileLoadFailed(state, action: PayloadAction<{ message: string }>) {
      state.status = "error";
      state.error = action.payload.message;
      state.user = null;
      state.profileFetchedAt = null;
    },

    refreshStarted(state) {
      state.status = "refreshing";
      state.error = null;
    },

    refreshSucceeded(state) {
      if (state.user) {
        state.status = "authenticated";
      }
    },

    enrichmentUpdated(state, action: PayloadAction<Partial<UserEnrichment>>) {
      if (!state.user) return;
      state.user = {
        ...state.user,
        enrichment: {
          ...state.user.enrichment,
          ...action.payload,
        },
      };
    },

    setUnauthenticated(state) {
      state.status = "unauthenticated";
      state.user = null;
      state.error = null;
      state.profileFetchedAt = null;
      state.session = null;
    },

    setAuthError(state, action: PayloadAction<{ message: string }>) {
      state.status = "error";
      state.error = action.payload.message;
    },

    clearAuthError(state) {
      state.error = null;
      if (state.status === "error") {
        state.status = "unauthenticated";
      }
    },
  },
});

export const authActions = authSlice.actions;
export default authSlice.reducer;
